import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

/**
 * A status badge names a *meaning*, never a colour.
 *
 * `tone="available"` survives a repaint; a green pill hardcoded in twenty
 * components does not (Frontend Spec §29).
 */
export type BadgeTone =
  | "available"
  | "reserved"
  | "loaned"
  | "repair"
  | "out"
  | "unknown"
  | "info"
  | "warning";

const TONES: Record<BadgeTone, string> = {
  available: "bg-success-subtle text-status-available border-status-available/30",
  reserved: "bg-warning-subtle text-status-reserved border-status-reserved/30",
  loaned: "bg-info-subtle text-status-loaned border-status-loaned/30",
  repair: "bg-surface-raised text-status-repair border-status-repair/30",
  out: "bg-danger-subtle text-status-out border-status-out/30",
  unknown: "bg-surface-raised text-ink-subtle border-border",
  info: "bg-info-subtle text-info border-info/30",
  warning: "bg-warning-subtle text-warning border-warning/30",
};

export function StatusBadge({
  tone = "unknown",
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
